import { BuilderComponent as Component, Builder } from '@builder.io/react';
import { useRouter } from 'next/router';
import { createContext, Dispatch, SetStateAction, useContext } from 'react';
import { useDeepCompareEffect } from '../hooks/useDeepCompareEffect';
import { DataSets, PageContent } from '../builder';
import {
  DEFAULT_ACCOUNT,
  DEFAULT_SESSION,
  DEFAULT_EXTENDED_USER_PROFILE,
  STATIC_ACCOUNT,
  STATIC_EXTENDED_SESSION,
  STATIC_EXTENDED_USER_PROFILE,
  useCurrentAccount,
  useSession,
  useUserProfile,
} from '../rest';
import { bindAsyncDataToHTML } from './RichTextHtml/RichTextHtml';
import { PageData } from './Page';

type Props = {
  content: PageContent;
  setPageData: Dispatch<SetStateAction<PageData>>;
};

export const ComponentContext = createContext<{
  asyncData: Record<string, any>;
  dataSets?: DataSets;
}>({ asyncData: {} });

export const useComponentContext = () => {
  return useContext(ComponentContext);
};

export const PageBuilderComponent = ({ content, setPageData }: Props) => {
  const asyncData = useAsyncData(content.dataSets) || {};

  const bindAsyncDataToAlertBanner = (data: PageData): PageData => {
    if (!data?.alertBanner?.body) return data;
    return {
      ...data,
      alertBanner: {
        ...data.alertBanner,
        parsedBody: bindAsyncDataToHTML(
          data?.alertBanner?.body || '',
          asyncData,
        ),
      },
    };
  };

  useDeepCompareEffect(() => {
    if (content.data?.alertBanner?.body && content.dataSets) {
      setPageData(bindAsyncDataToAlertBanner);
    }
  }, [asyncData, content.data?.alertBanner?.body, setPageData]);

  return (
    <ComponentContext.Provider
      value={{ asyncData, dataSets: content.dataSets }}
    >
      <Component
        contentLoaded={(data) => {
          if (Builder.isEditing || Builder.isPreviewing) {
            setPageData(bindAsyncDataToAlertBanner(data));
          }
        }}
        content={content}
        data={asyncData}
        model={Builder.previewingModel || content?.model}
      />
    </ComponentContext.Provider>
  );
};

export const useAsyncData = (dataSets?: DataSets) => {
  const router = useRouter();

  const { data: user } = useSession(!!dataSets?.user);
  const { data: userProfile } = useUserProfile(!!dataSets?.userProfile);
  const { data: currentAccount } = useCurrentAccount(
    !!dataSets?.currentAccount,
  );

  if (Builder.isEditing || Builder.isPreviewing) {
    return {
      currentAccount: STATIC_ACCOUNT,
      user: STATIC_EXTENDED_SESSION,
      userProfile: STATIC_EXTENDED_USER_PROFILE,
      queryParameters: {
        demo_source: 'example_demo_source',
        utm_campaign: 'example_utm_campaign',
        utm_medium: 'example_utm_medium',
        utm_source: 'example_utm_source',
      },
    };
  }

  return {
    currentAccount: { ...DEFAULT_ACCOUNT, ...currentAccount },
    user: { ...DEFAULT_SESSION, ...user },
    userProfile: { ...DEFAULT_EXTENDED_USER_PROFILE, ...userProfile },
    queryParameters: {
      demo_source: router.query.demo_source ?? '',
      utm_campaign: router.query.utm_campaign ?? '',
      utm_medium: router.query.utm_medium ?? '',
      utm_source: router.query.utm_source ?? '',
    },
  };
};
